// Parses a Catapult "Focus" export CSV (jump/period tagging export) into a
// structured shape the CatapultReport component can render and synchronise
// with volleyball match events.
//
// The export has one header row then two kinds of data rows, both with the
// same 14 columns:
//   Category, Tag, start_time, event_time, end_time, Athlete, Position,
//   Period, athlete_id, period_id, "OF Event", "Height (m)", Duration,
//   "epoch (ms)"
// - A "jump" row: Category/Athlete = "<shirt number> <last name>" (e.g.
//   "11 Coneo"), Tag/"OF Event" = "IMA Jump", start_time/event_time/end_time
//   are epoch MILLISECONDS (event_time is the actual jump instant; start/end
//   pad a few seconds either side for clip review), "Height (m)" and
//   Duration are the jump's peak height (metres) and hang time (seconds).
// - A "period" row: Category = "OF Period", Tag/Period = the set/interval
//   name (e.g. "1st set"), start_time/end_time bound that period, period_id
//   is the same id jump rows within that period carry.

import {
    kSkillBlock,
    kSkillServe,
    kSkillSet,
    kSkillSettersCall,
    kSkillSpike,
} from "./Constants";
import { myunzip, myzip } from "./zip";

// Skills that virtually always involve a jump -- same set the sibling
// catapult-vb project uses (DVW_MATCH_TYPES in src/utils/dvwSync.js) to find
// the clock offset between a jump sensor's timestamps and the match's own.
const kJumpLikeSkills = new Set([
  kSkillServe,
  kSkillSet,
  kSkillSpike,
  kSkillBlock,
  kSkillSettersCall,
]);
const kSyncToleranceSec = 2.0;

// Minimal RFC4180-ish CSV line splitter (handles quoted fields with embedded
// commas/quotes) -- no external dependency needed for this fixed-shape file.
function parseCsvLine(line) {
  const fields = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      fields.push(cur);
      cur = "";
    } else {
      cur += c;
    }
  }
  fields.push(cur);
  return fields;
}

// "11 Coneo" -> { shirtNumber: 11, lastName: "Coneo" }. Falls back to a null
// shirt number (name-only matching) when the leading number is missing.
function parseAthleteLabel(label) {
  const m = /^(\d+)\s+(.*)$/.exec((label ?? "").trim());
  if (m) {
    return { shirtNumber: parseInt(m[1], 10), lastName: m[2].trim() };
  }
  return { shirtNumber: null, lastName: (label ?? "").trim() };
}

function toSec(msStr) {
  const ms = parseFloat(msStr);
  return Number.isFinite(ms) ? ms / 1000 : null;
}

export function parseCatapultFocusCSV(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) {
    return { periods: [], jumps: [] };
  }
  const header = parseCsvLine(lines[0]).map((h) => h.trim());
  const idx = (name) => header.indexOf(name);
  const iCategory = idx("Category");
  const iStart = idx("start_time");
  const iEvent = idx("event_time");
  const iEnd = idx("end_time");
  const iPeriod = idx("Period");
  const iAthleteId = idx("athlete_id");
  const iPeriodId = idx("period_id");
  const iOfEvent = idx("OF Event");
  const iHeight = idx("Height (m)");
  const iDuration = idx("Duration");

  const periods = [];
  const jumps = [];

  for (let i = 1; i < lines.length; i++) {
    const f = parseCsvLine(lines[i]);
    if (f.length < header.length) continue;
    const ofEvent = f[iOfEvent]?.trim();
    const startSec = toSec(f[iStart]);
    const eventSec = toSec(f[iEvent]);
    const endSec = toSec(f[iEnd]);

    if (f[iCategory]?.trim() === "OF Period") {
      periods.push({
        name: f[iPeriod]?.trim() ?? "",
        periodId: f[iPeriodId]?.trim() ?? "",
        startSec,
        endSec,
      });
      continue;
    }

    if (ofEvent === "IMA Jump") {
      const { shirtNumber, lastName } = parseAthleteLabel(f[iCategory]);
      const heightM = parseFloat(f[iHeight]);
      const durationSec = parseFloat(f[iDuration]);
      jumps.push({
        shirtNumber,
        lastName,
        athleteId: f[iAthleteId]?.trim() ?? "",
        periodId: f[iPeriodId]?.trim() ?? "",
        periodName: f[iPeriod]?.trim() ?? "",
        startSec,
        eventSec,
        endSec,
        heightM: Number.isFinite(heightM) ? heightM : null,
        durationSec: Number.isFinite(durationSec) ? durationSec : null,
      });
    }
  }

  jumps.sort((a, b) => (a.eventSec ?? 0) - (b.eventSec ?? 0));
  periods.sort((a, b) => (a.startSec ?? 0) - (b.startSec ?? 0));
  return { periods, jumps };
}

// Groups jumps by shirt number (falls back to last name when no shirt number
// was present in the export) for per-player display.
export function groupJumpsByAthlete(jumps) {
  const groups = new Map();
  for (const j of jumps) {
    const key = j.shirtNumber != null ? `#${j.shirtNumber}` : `name:${j.lastName}`;
    if (!groups.has(key)) {
      groups.set(key, {
        shirtNumber: j.shirtNumber,
        lastName: j.lastName,
        jumps: [],
      });
    }
    groups.get(key).jumps.push(j);
  }
  return [...groups.values()].sort((a, b) => (a.shirtNumber ?? 999) - (b.shirtNumber ?? 999));
}

// Finds the constant time offset (imaTime - matchTime, in seconds) that
// maximises the number of Catapult jump timestamps landing within
// `toleranceSec` of a match "jump-like" event timestamp -- same
// histogram-binning technique used in the sibling catapult-vb project
// (src/utils/dvwSync.js computeSyncOffset), just reading jump times from a
// parsed CSV instead of a live Catapult API fetch.
export function computeSyncOffset(matchEventTimesSec, imaJumpTimesSec, toleranceSec = 2.0) {
  if (!matchEventTimesSec.length || !imaJumpTimesSec.length) return null;
  const counts = new Map();
  for (const d of matchEventTimesSec) {
    for (const i of imaJumpTimesSec) {
      const bin = Math.round((i - d) / toleranceSec) * toleranceSec;
      counts.set(bin, (counts.get(bin) ?? 0) + 1);
    }
  }
  let bestBin = 0;
  let bestCount = 0;
  for (const [bin, count] of counts) {
    if (count > bestCount) {
      bestCount = count;
      bestBin = bin;
    }
  }
  const near = [];
  for (const d of matchEventTimesSec) {
    for (const i of imaJumpTimesSec) {
      const c = i - d;
      if (Math.abs(c - bestBin) < toleranceSec) near.push(c);
    }
  }
  near.sort((a, b) => a - b);
  const refined = near.length > 0 ? near[Math.floor(near.length / 2)] : bestBin;
  let matchCount = 0;
  for (const d of matchEventTimesSec) {
    if (imaJumpTimesSec.some((i) => Math.abs(i - (d + refined)) < toleranceSec)) {
      matchCount++;
    }
  }
  return { offset: refined, matchCount, matchTotal: matchEventTimesSec.length };
}

// Finds the jump (from a single player's jump list) whose event time is
// closest to `targetSec` (already offset-adjusted into the sensor's clock),
// or null if none falls within `toleranceSec`.
export function findNearestJump(jumps, targetSec, toleranceSec = 1.0) {
  let best = null;
  let bestDiff = Infinity;
  for (const j of jumps) {
    if (j.eventSec == null) continue;
    const diff = Math.abs(j.eventSec - targetSec);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = j;
    }
  }
  return best && bestDiff <= toleranceSec ? best : null;
}

// Serialises parsed Catapult Focus data ({periods, jumps}) so it can be
// stored alongside a match's other saved data (see fsStoreSession).
export function compressCatapultFocusData(parsed) {
  if (!parsed) return null;
  return myzip(JSON.stringify(parsed));
}

// Reverses compressCatapultFocusData. Returns null if it can't be read (e.g.
// missing/corrupt data) rather than throwing.
export function decompressCatapultFocusData(compressed) {
  if (!compressed) return null;
  try {
    return JSON.parse(myunzip(compressed));
  } catch {
    return null;
  }
}

export function playerForShirtNumber(match, shirtNumber, teamSide) {
  if (shirtNumber == null) return null;
  const all =
    teamSide === "A"
      ? match.teamA?.players ?? []
      : teamSide === "B"
        ? match.teamB?.players ?? []
        : [...(match.teamA?.players ?? []), ...(match.teamB?.players ?? [])];
  return (
    all.find((p) => Number(p.shirtNumber ?? p.ShirtNumber) === shirtNumber) ??
    null
  );
}

function isPlayerOnSide(match, player, teamSide) {
  if (!player || !teamSide) return true; // unknown side -- don't filter anything out
  const roster = teamSide === "A" ? match.teamA?.players : match.teamB?.players;
  return (roster ?? []).some((p) => p === player || p.Guid === player.Guid);
}

function namesLooselyMatch(a, b) {
  const na = (a ?? "").toLowerCase().trim();
  const nb = (b ?? "").toLowerCase().trim();
  if (!na || !nb) return false;
  return na === nb || na.includes(nb) || nb.includes(na);
}

// A Catapult Focus export only ever covers ONE team's players, not both --
// but shirt numbers collide across teams (e.g. both sides can have a "#4"),
// so simply matching by shirt number against the whole match is unreliable.
// Compares the CSV's athletes (shirt number + last name) against each team's
// roster and returns which side the file most likely belongs to, preferring
// LAST-NAME-CONFIRMED matches (strong evidence) over raw shirt-number overlap
// (weak evidence, since that alone can coincidentally match either team).
// `confident` is false when the evidence doesn't clearly favour one side --
// callers should ask the user to pick in that case.
export function detectCatapultTeamSide(match, jumps) {
  const csvAthletes = new Map(); // shirtNumber -> lastName
  for (const j of jumps ?? []) {
    if (j.shirtNumber == null) continue;
    if (!csvAthletes.has(j.shirtNumber)) csvAthletes.set(j.shirtNumber, j.lastName ?? "");
  }
  const total = csvAthletes.size;
  if (total === 0) return { side: null, confident: false, scoreA: 0, scoreB: 0, total: 0 };

  const scoreTeam = (players) => {
    let shirtMatches = 0;
    let nameMatches = 0;
    for (const [shirt, lastName] of csvAthletes) {
      const p = (players ?? []).find((pl) => Number(pl.shirtNumber ?? pl.ShirtNumber) === shirt);
      if (!p) continue;
      shirtMatches++;
      if (namesLooselyMatch(p.LastName, lastName)) nameMatches++;
    }
    return { shirtMatches, nameMatches };
  };

  const a = scoreTeam(match.teamA?.players);
  const b = scoreTeam(match.teamB?.players);
  // Prefer name-confirmed matches (strong evidence); only fall back to raw
  // shirt-number overlap (weak evidence -- numbers collide across teams) if
  // NEITHER team has a single name-confirmed match at all.
  const useNames = a.nameMatches > 0 || b.nameMatches > 0;
  const scoreA = useNames ? a.nameMatches : a.shirtMatches;
  const scoreB = useNames ? b.nameMatches : b.shirtMatches;
  const winner = scoreA === scoreB ? null : scoreA > scoreB ? "A" : "B";
  const winnerScore = Math.max(scoreA, scoreB);
  const loserScore = Math.min(scoreA, scoreB);
  // Confident only when the winner accounts for at least half the CSV's
  // distinct athletes AND clearly outscores the other side (not a near-even
  // split that could just be coincidental shirt-number overlap).
  const confident =
    winner != null &&
    winnerScore >= Math.ceil(total * 0.5) &&
    winnerScore >= loserScore * 2 + 1;

  return { side: confident ? winner : null, confident, scoreA, scoreB, total, usedNames: useNames };
}

// Synchronises a match's own "jump-like" events (serve/set/spike/block/
// setter's call) with a previously-loaded Catapult Focus export attached as
// match.catapultFocusData ({periods, jumps}): attaches imaHeightM/
// imaDurationSec onto each matched event and each player's own jumps onto
// their player object (all mutated in place, same `match` reference callers
// already hold). Called both as soon as a match loads (Session.jsx, so Video
// Analysis has enriched events even if the Catapult tab is never opened) and
// whenever CatapultReport parses a fresh CSV. `teamSide` ( "A"/"B", from
// detectCatapultTeamSide or the user's own pick) restricts matching to that
// team's roster only -- a Focus export covers one team, and shirt numbers
// collide across teams, so matching against both would corrupt the sync.
// Returns the number of events enriched (0 if there's no saved jump data or
// no match events to sync).
export function enrichMatchWithCatapultData(match, teamSide) {
  const jumps = match?.catapultFocusData?.jumps;
  if (!jumps?.length || !match.events) return { perPlayer: {}, overall: null, enrichedCount: 0 };

  const groups = groupJumpsByAthlete(jumps);
  for (const g of groups) {
    const player = playerForShirtNumber(match, g.shirtNumber, teamSide);
    if (player) player.catapultJumps = g.jumps;
  }

  const matchJumpEvents = match.events.filter(
    (ev) =>
      kJumpLikeSkills.has(ev.EventType) &&
      ev.TimeStamp &&
      !isNaN(ev.TimeStamp.getTime()) &&
      isPlayerOnSide(match, ev.Player, teamSide)
  );
  if (matchJumpEvents.length === 0) {
    return { perPlayer: {}, overall: null, enrichedCount: 0 };
  }

  const perPlayer = {};
  for (const g of groups) {
    if (g.shirtNumber == null) continue;
    const playerTimes = matchJumpEvents
      .filter(
        (ev) =>
          Number(ev.Player?.shirtNumber ?? ev.Player?.ShirtNumber) ===
          g.shirtNumber
      )
      .map((ev) => ev.TimeStamp.getTime() / 1000);
    const imaTimes = g.jumps.map((j) => j.eventSec).filter((v) => v != null);
    if (playerTimes.length === 0 || imaTimes.length === 0) continue;
    const res = computeSyncOffset(playerTimes, imaTimes, kSyncToleranceSec);
    if (res) perPlayer[g.shirtNumber] = res;
  }
  const allPlayerTimes = matchJumpEvents.map(
    (ev) => ev.TimeStamp.getTime() / 1000
  );
  const allImaTimes = jumps.map((j) => j.eventSec).filter((v) => v != null);
  const overall = computeSyncOffset(allPlayerTimes, allImaTimes, kSyncToleranceSec);

  let enrichedCount = 0;
  for (const ev of matchJumpEvents) {
    const shirt = Number(ev.Player?.shirtNumber ?? ev.Player?.ShirtNumber);
    const group = groups.find((g) => g.shirtNumber === shirt);
    if (!group) continue;
    const offset = perPlayer[shirt]?.offset ?? overall?.offset;
    if (offset == null) continue;
    const targetSec = ev.TimeStamp.getTime() / 1000 + offset;
    const jump = findNearestJump(group.jumps, targetSec, kSyncToleranceSec);
    if (jump) {
      ev.imaHeightM = jump.heightM;
      ev.imaDurationSec = jump.durationSec;
      enrichedCount++;
    } else {
      delete ev.imaHeightM;
      delete ev.imaDurationSec;
    }
  }
  return { perPlayer, overall, enrichedCount };
}
